/*
 * Copyright (c) 2024 CalAmp Corp. All Rights Reserved
 */
import React from 'react';
import {useSelector} from 'react-redux';
import {makeStyles} from '@material-ui/core';
import {translate} from '@ion/core/src/locale/locale';
import Tooltip from '@ion/dls/src/components/tooltip/Tooltip';
import {UrgentIcon} from '@ion/dls/src/icons/UrgentIcon';
import ConditionalRender from '@ion/dls/src/render/ConditionalRender';
import {dlsAlt, dlsColor} from '@ion/dls/src/theme/theme';
import {DLSTheme} from '@ion/dls/src/types/types';
import {jobSearchEngineTabsSelector} from '@/store/selectors/jobsearchengine';
import {isJobTabLimitReached} from '../JobSearchEngineUtils';
import WatchJobBell, {useWatchBellVisible} from './WatchJobBell';

export interface JobIdCellProps {
  jobId: string;
  handleClick: Function;
  status: string;
  disabled?: boolean;
};

export enum JobStatus {
  FAILED = 'Failed'
}

export const useStyles = makeStyles((theme: DLSTheme) => ({
  container : {
    height     : '100%',
    display    : 'flex',
    alignItems : 'center'
  },
  cell : {
    display        : 'flex',
    justifyContent : 'center',
    alignItems     : 'center',
    gap            : '10px',
    ...dlsAlt('typography', 'label1'),
    color          : dlsColor('grey1'),
    textDecoration : 'underline',
    cursor         : 'pointer'
  },
  cellDisabled : {
    display        : 'flex',
    justifyContent : 'center',
    alignItems     : 'center',
    gap            : '10px',
    ...dlsAlt('typography', 'label1'),
    color          : dlsColor('grey3'),
    cursor         : 'initial'
  },
  tooltip : {
    padding  : 0,
    maxWidth : 200
  },
  icon : {
    display        : 'flex',
    alignItems     : 'center',
    justifyContent : 'center',
    flex           : '0 0 20px',
    width          : 20,
    height         : 20
  }
}));

/**
 * JobIdCell is a component that provides the user the ability to open a new
 * tab in the JobSearchEngine component.
 * 
 * If the disabled prop is `true` - we prevent the action and display a tooltip
 * to inform the user they must close an existing tab to open a new one.
 * 
 * ```typescript
 * import JobIdCell from './cells/JobIdCell';
 * 
 * const columnComponents = useMemo(() => ({
 *  jobIdRenderer  : (data) => (
 *     <JobIdCell jobId={data?.value}
 *      disabled={isTabMaxLimitReached}
 *      status={data?.data?.progressStatus}
 *      handleClick={handleJobClick}/>
 *  )
  }), [handleJobClick, isTabMaxLimitReached]);

  ```
 */
export const JobIdCell = ({
  jobId,
  handleClick,
  status
}: JobIdCellProps) => {
  const classes     = useStyles();
  const {FAILED}    = JobStatus;
  const tabs        = useSelector(jobSearchEngineTabsSelector);
  const disabled    = isJobTabLimitReached(tabs);
  const bellVisible = useWatchBellVisible(jobId, status);

  const TooltipMessage = function() {
    return (
      <div className={classes.tooltip}>
        <p>{translate('jobsearchengine:tooltip.maxInfo')}</p>
        <p>{translate('jobsearchengine:tooltip.maxAction')}</p>
      </div>
    );
  };

  const CellIcons = function() {
    return (
      <>
        <WatchJobBell jobId={jobId} status={status} />
        <ConditionalRender condition={!bellVisible && status === FAILED}>
          <span className={classes.icon}>
            <UrgentIcon />
          </span>
        </ConditionalRender>
      </>
    );
  };

  return (
    <>
      <ConditionalRender condition={disabled}>
        <Tooltip title={TooltipMessage()} placement='right' arrow>
          <span className={classes.container}>
            <span className={classes.cellDisabled}>
              <span>{jobId}</span>
              {CellIcons()}
            </span>
          </span>
        </Tooltip>
      </ConditionalRender>
      <ConditionalRender condition={!disabled}>
        <div className={classes.container} onClick={function() { handleClick(jobId); }}>
          <div className={classes.cell}>
            <span>{jobId}</span>
            {CellIcons()}
          </div>
        </div>
      </ConditionalRender>
    </>
  );
};

export default JobIdCell;
